import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  TableBody,
  TableHead,
  TableRow,
  useTheme,
  TableContainer,
  Checkbox,
} from '@mui/material';
import {
  StyledTableCellTwo,
  StyledTableCellWithoutLeftTwo,
  StyledTableCellWithoutRightTwo,
  StyledTableCellWithoutLeftRightTwo,
} from 'routes/Main/style';
import SectionTitle from '../components/SectionTitle';

import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

const radioId = [
  'gender',
  'age',
  'weight',
  'incontinence',
  'skin',
  'mobility',
  'appetite',
  'special_risk',
];
const contentLabel = [
  {
    id: 1,
    ko: '성별',
    desc: ['남성', '여성'],
  },
  {
    id: 2,
    ko: '연령',
    desc: ['14 - 49세', '50 - 64세', '65 - 74세', '75 - 80세', '81+세'],
  },
  {
    id: 3,
    ko: '체격 / 체중',
    desc: ['평균', '평균이상', '비만', '평균이하'],
  },
  {
    id: 4,
    ko: '실금',
    desc: [
      '도관 착용',
      '때때로 실금',
      '도관이 있지만 대변실금',
      '대변 및 요실금',
    ],
  },
  {
    id: 5,
    ko: '비정상 피부상태 (중복 선택)',
    desc: [
      '위축된, 티슈페이퍼 같은',
      '건조',
      '부종성의',
      '냉습',
      '변색된',
      '파손이 존재',
    ],
  },
  {
    id: 6,
    ko: '움직임',
    desc: [
      '완전한',
      '안절부절',
      '무욕, 무감동',
      '제한된',
      '견인장치 사용 중',
      '휠체어 신세를 지고 있는',
    ],
  },
  {
    id: 7,
    ko: '식욕',
    desc: ['평균', '나쁜', '비위관/유동식만', '식욕부진'],
  },
  {
    id: 8,
    ko: '특수위험요인 (중복 선택)',
    desc: [
      '말기 악액질',
      '심부전',
      '말초혈관질환',
      '빈혈',
      '흡연자',
      '당뇨병성 신경결손',
      '다발경화증 신경결손',
      'CVA 신경결손',
      '운동 감각 신경결손',
      '하반신마비 신경결손',
      '최근 허리이하의 정형외과적 수술',
      '최근 척수마취 (수술대에서 2시간 이상)',
      '세포독성약물 사용중',
      '고농도 코르티코스테로이드 사용중',
      '항염증약 사용중',
    ],
  },
];
const scoreLabel = [
  { score: '10-14점 :', label: '위험' },
  { score: '15-19점 :', label: '고위험' },
  { score: '20-92점 :', label: '매우 고위험' },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
}
const BedScoreFourContents = (props: Props) => {
  const { palette } = useTheme();

  const { disabled, setValue, getValues, register } = props;

  const [sumValue, setSumValue] = useState(0);

  const [mobilityCheckedList, setMobilityCheckedList] = useState<Array<number>>(
    []
  );

  const mobilityOnCheckedItem = useCallback(
    (checked: boolean, item: number) => {
      if (checked) {
        setMobilityCheckedList(prev => [...prev, item]);
      } else if (!checked) {
        setMobilityCheckedList(
          mobilityCheckedList.filter((el, i) => el !== item)
        );
      }
    },
    [mobilityCheckedList]
  );

  const mobilitySumChecked = [
    getValues('skin00') ? 1 : 0,
    getValues('skin01') ? 1 : 0,
    getValues('skin02') ? 1 : 0,
    getValues('skin03') ? 1 : 0,
    getValues('skin04') ? 2 : 0,
    getValues('skin05') ? 3 : 0,
  ].reduce((a, cur) => {
    if (cur === undefined) return 0;
    return cur ? a + cur : a;
  }, 0);

  const [cognitionCheckedList, setCognitionCheckedList] = useState<
    Array<number>
  >([]);

  const cognitionOnCheckedItem = useCallback(
    (checked: boolean, item: number) => {
      if (checked) {
        setCognitionCheckedList(prev => [...prev, item]);
      } else if (!checked) {
        setCognitionCheckedList(
          cognitionCheckedList.filter((el, i) => el !== item)
        );
      }
    },
    [cognitionCheckedList]
  );

  const cognitionSumChecked = [
    getValues('special_risk00') ? 8 : 0,
    getValues('special_risk01') ? 5 : 0,
    getValues('special_risk02') ? 5 : 0,
    getValues('special_risk03') ? 2 : 0,
    getValues('special_risk04') ? 1 : 0,
    getValues('special_risk05') ? 5 : 0,
    getValues('special_risk06') ? 5 : 0,
    getValues('special_risk07') ? 5 : 0,
    getValues('special_risk08') ? 5 : 0,
    getValues('special_risk09') ? 5 : 0,
    getValues('special_risk010') ? 5 : 0,
    getValues('special_risk011') ? 5 : 0,
    getValues('special_risk012') ? 4 : 0,
    getValues('special_risk013') ? 4 : 0,
    getValues('special_risk014') ? 4 : 0,
  ].reduce((a, cur) => {
    if (cur === undefined) return 0;
    return cur ? a + cur : a;
  }, 0);

  const calculateSumValue = () => {
    setSumValue(
      radioId.reduce((acc, cur) => {
        if (cur === 'gender' || cur === 'age') {
          const value = Number(getValues(cur)) + 1;
          return value ? acc + value : acc;
        } else if (cur === 'evacuation') {
          if (Number(getValues(cur)) === 1 || Number(getValues(cur)) === 3) {
            const value = Number(getValues(cur)) + 1;
            return value ? acc + value : acc;
          }
        } else if (cur === 'drug' && Number(getValues(cur)) > 0) {
          const value = Number(getValues(cur)) * 2 + 1;
          return value ? acc + value : acc;
        }
        const value = Number(getValues(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    calculateSumValue();
  };

  useEffect(() => {
    calculateSumValue();
  }, []);

  return (
    <>
      <SectionTitle title="Waterlow Scale" />
      <Box sx={{ width: '98%', margin: '30px 20px' }}>
        <TableContainer>
          <TableHead>
            <TableRow>
              <StyledTableCellTwo
                sx={{
                  padding: '16px',
                  textAlign: 'center',
                }}
              >
                범주
              </StyledTableCellTwo>
              <StyledTableCellWithoutLeftTwo
                sx={{ width: '50px', paddingLeft: '10px' }}
              >
                점수
              </StyledTableCellWithoutLeftTwo>
              <StyledTableCellWithoutLeftTwo
                sx={{ padding: '16px', paddingLeft: '103px' }}
              >
                항목
              </StyledTableCellWithoutLeftTwo>
              <StyledTableCellTwo></StyledTableCellTwo>
            </TableRow>
          </TableHead>

          <TableBody>
            {contentLabel.map(
              (content: { id: number; ko: string; desc: string[] }, i) => {
                if (i === 4 || i === 7) {
                  return (
                    <TableRow>
                      <StyledTableCellTwo
                        align="center"
                        sx={{
                          padding: '16px',
                          width: '300px',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {content.ko}
                      </StyledTableCellTwo>
                      <StyledTableCellWithoutRightTwo>
                        {content.desc.map((item, i) => {
                          if (content.id === 8) {
                            if (item === '말기 악액질') {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    8점
                                  </Box>
                                </TableRow>
                              );
                            } else if (item === '빈혈') {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    2점
                                  </Box>
                                </TableRow>
                              );
                            } else if (item === '흡연자') {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    1점
                                  </Box>
                                </TableRow>
                              );
                            } else if (i > 11) {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    4점
                                  </Box>
                                </TableRow>
                              );
                            }
                            return (
                              <TableRow
                                sx={{ height: '44px', lineHeight: '44px' }}
                              >
                                <Box
                                  sx={{ minWidth: '50px', textAlign: 'center' }}
                                >
                                  5점
                                </Box>
                              </TableRow>
                            );
                          } else if (content.id === 5) {
                            if (i < 4) {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    1 점
                                  </Box>
                                </TableRow>
                              );
                            }
                            return (
                              <TableRow
                                sx={{ height: '44px', lineHeight: '44px' }}
                              >
                                <Box
                                  sx={{ minWidth: '50px', textAlign: 'center' }}
                                >
                                  {i - 2}점
                                </Box>
                              </TableRow>
                            );
                          }
                        })}
                      </StyledTableCellWithoutRightTwo>
                      <StyledTableCellWithoutLeftRightTwo>
                        {content.desc.map((v, i) => (
                          <TableRow
                            sx={{
                              height: '44px',
                              lineHeight: '44px',
                            }}
                          >
                            <Box
                              sx={{
                                paddingLeft: '100px',
                                minWidth: '900px',
                              }}
                            >
                              {v}
                            </Box>
                          </TableRow>
                        ))}
                      </StyledTableCellWithoutLeftRightTwo>
                      <StyledTableCellWithoutLeftTwo>
                        {content.desc.map((item, i) => (
                          <TableRow
                            sx={{
                              height: '44px',
                              lineHeight: '44px',
                              textAlign: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: '200px',
                                paddingLeft: '50px',
                                paddingRight: '70px',
                              }}
                            >
                              <Checkbox
                                disabled={disabled}
                                defaultChecked={
                                  content.id === 5
                                    ? Boolean(getValues(`skin0${i}`))
                                    : Boolean(getValues(`special_risk0${i}`))
                                }
                                {...register(
                                  content.id === 5
                                    ? `skin0${i}`
                                    : `special_risk0${i}`
                                )}
                                onChange={(e, checked) => {
                                  if (content.id === 5) {
                                    setValue(`skin0${i}`, checked);
                                    mobilityOnCheckedItem(e.target.checked, i);
                                  } else if (content.id === 8) {
                                    setValue(`special_risk0${i}`, checked);
                                    cognitionOnCheckedItem(e.target.checked, i);
                                  }
                                }}
                              />
                            </Box>
                          </TableRow>
                        ))}
                      </StyledTableCellWithoutLeftTwo>
                    </TableRow>
                  );
                }

                return (
                  <TableRow>
                    <StyledTableCellTwo
                      align="center"
                      sx={{
                        padding: '16px',
                        width: '300px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {content.ko}
                    </StyledTableCellTwo>
                    <StyledTableCellWithoutRightTwo>
                      {content.desc.map((item, i) => {
                        if (content.id === 1 || content.id === 2) {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {i + 1}점
                              </Box>
                            </TableRow>
                          );
                        } else if (item === '급박/빈번과 실금') {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {4}점
                              </Box>
                            </TableRow>
                          );
                        }
                        return (
                          <TableRow sx={{ height: '44px', lineHeight: '44px' }}>
                            <Box sx={{ minWidth: '50px', textAlign: 'center' }}>
                              {i}점
                            </Box>
                          </TableRow>
                        );
                      })}
                    </StyledTableCellWithoutRightTwo>
                    <StyledTableCellWithoutLeftRightTwo>
                      {content.desc.map((v, i) => (
                        <TableRow
                          sx={{
                            height: '44px',
                            lineHeight: '44px',
                          }}
                        >
                          <Box sx={{ paddingLeft: '100px', minWidth: '500px' }}>
                            {v}
                          </Box>
                        </TableRow>
                      ))}
                    </StyledTableCellWithoutLeftRightTwo>
                    <StyledTableCellWithoutLeftTwo>
                      <RadioGroup
                        name={radioId[content.id - 1]}
                        defaultValue={Number(
                          getValues(radioId[content.id - 1])
                        )}
                      >
                        {content.desc.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                height: '44px',
                                lineHeight: '44px',
                                textAlign: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '200px',
                                  paddingLeft: '50px',
                                  paddingRight: '70px',
                                }}
                              >
                                <Radio
                                  disabled={disabled}
                                  name={radioId[content.id - 1]}
                                  value={i}
                                  onChange={handleChange}
                                />
                              </Box>
                            </TableRow>
                          );
                        })}
                      </RadioGroup>
                    </StyledTableCellWithoutLeftTwo>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </TableContainer>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-end'}
          sx={{ marginTop: '20px' }}
        >
          <Typography
            gutterBottom
            minWidth={115}
            fontWeight={700}
            variant="subtitle1"
          >
            합계: {sumValue + mobilitySumChecked + cognitionSumChecked}점
          </Typography>
          <Typography
            minWidth={115}
            variant="caption"
            sx={{ color: `${palette.primary.main}` }}
          >
            {scoreLabel.map(({ score, label }) => (
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  {score}
                </Box>
                {label}
              </Typography>
            ))}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default BedScoreFourContents;
